import { ENUM__connection_state } from '@/enums/ENUM__connection_state';
import { ENUM__page_type } from '@/enums/ENUM__page_type';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_app_info, use_slice_network, use_slice_ui, use_slice_user } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { local_app_info_upsert } from '@/state/app_info/local_app_info_upsert';
import { set_timeout } from '@/utils/utils_client';
import { ENUM__user_role } from '@shared/dto/+user/entity/ENUM__user_role';
import { EMaintenanceStatus } from '@shared/dto/app_info/e_maintenance_status';
import { timestamp_ms } from '@shared/utils/utils_time';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_PRIMITIVE__progress_bar } from '../../../components/ui/UI_PRIMITIVE__progress_bar';

export function WATCHER__app_info_maintenance() {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const dispatch = use_dispatch();

  const { app_info } = use_slice_app_info(['app_info']);
  const { user_role } = use_slice_user(['user_role']);
  const { state } = use_slice_network(['state']);
  const { global__page_type } = use_slice_ui(['global__page_type']);

  const { toast, dismiss } = useToast();

  const ref_toast_id = useRef('');

  /*
   * EFFECTS
   */

  use_effect(() => {
    close_previous_toast();

    try_show_toast_maintenance_planned();

    try_show_toast_maintenance();

    return () => close_previous_toast();
  }, [app_info, state, global__page_type]);

  /*
   * METHODS
   */

  function close_previous_toast() {
    dismiss(ref_toast_id.current);
    ref_toast_id.current = '';
  }

  function try_show_toast_maintenance_planned() {
    if (state != ENUM__connection_state.CONNECTION_ESTABLISHED) return;
    if (app_info.maintenance_status != EMaintenanceStatus.PLANNED_TILL_RESTART_APP) return;

    let duration_before_maintenance = app_info.last_maintenance_started_at - timestamp_ms();

    duration_before_maintenance = Math.max(0, duration_before_maintenance);

    let timeout_id = set_timeout(update_app_info_local, duration_before_maintenance);

    const toast_data = toast({
      title: t(i18n_base_ui.toasts.app_info_maintenance_planned.title),
      duration: duration_before_maintenance,
      description: <UI_PRIMITIVE__progress_bar timeout_ms={duration_before_maintenance} />,
    });

    ref_toast_id.current = toast_data.id;

    return () => clearTimeout(timeout_id);
  }

  function update_app_info_local() {
    let app_info_new = app_info.clone({ maintenance_status: EMaintenanceStatus.ENABLED_TILL_RESTART_APP });

    dispatch(local_app_info_upsert(app_info_new));
  }

  function try_show_toast_maintenance() {
    if (!app_info.is_maintenance_mode_enabled()) return;

    if (user_role == ENUM__user_role.ADMIN || global__page_type == ENUM__page_type.AUTH) {
      show_toast_maintenance();
    } else {
      //do nothing because splash is already visible
    }
  }

  function show_toast_maintenance() {
    if (state != ENUM__connection_state.CONNECTION_ESTABLISHED) return;

    const toast_data = toast({
      title: t(i18n_base_ui.toasts.app_info_maintenance.title),
      duration: Infinity,
      hide_button_close: true,
      className: '!pointer-events-none cursor-default',
    });

    ref_toast_id.current = toast_data.id;
  }

  return null;
}
